import { useTranslation } from 'react-i18next';

const SkeletonRows = ({ columns, rows = 5 }) =>
  Array.from({ length: rows }).map((_, rowIndex) => (
    <tr key={`skeleton-${rowIndex}`} className="border-b border-beam-blue/10">
      {columns.map((column, columnIndex) => (
        <td key={column.key || columnIndex} className="px-4 py-4">
          <span className="block h-4 w-full max-w-[220px] animate-pulse rounded bg-beam-light" />
        </td>
      ))}
    </tr>
  ));

/**
 * Real table semantics at md and up, stacked cards below it. Replaces the three
 * hand-rolled CSS-grid lists the admin used to carry, none of which exposed a
 * column header to assistive tech.
 */
export const DataTable = ({
  caption,
  columns,
  rows,
  getRowId,
  selectable = false,
  selectedIds = [],
  onToggleRow,
  onToggleAll,
  allSelected = false,
  isFetching = false,
  isLoading = false,
  empty,
  renderCard,
  toolbar,
  footer,
}) => {
  const { t } = useTranslation();
  const columnCount = columns.length + (selectable ? 1 : 0);

  return (
    <section className="overflow-hidden rounded-lg border border-beam-blue/10 bg-white shadow-sm">
      {toolbar ? (
        <div className="flex flex-col gap-3 border-b border-beam-blue/10 p-4 sm:flex-row sm:items-center sm:justify-between">
          {toolbar}
        </div>
      ) : null}

      <div
        aria-busy={isFetching || undefined}
        className={`transition-opacity ${isFetching && !isLoading ? 'opacity-60' : 'opacity-100'}`}
      >
        {/* Table view */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full border-collapse text-left">
            {caption ? <caption className="sr-only">{caption}</caption> : null}
            <thead>
              <tr className="border-b border-beam-blue/10 bg-beam-light/50">
                {selectable ? (
                  <th scope="col" className="w-12 px-4 py-3">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={onToggleAll}
                      className="focus-ring h-4 w-4 accent-beam-blue"
                      aria-label={t('admin.selectPage')}
                    />
                  </th>
                ) : null}
                {columns.map((column) => (
                  <th
                    key={column.key}
                    scope="col"
                    className={`px-4 py-3 text-[11px] font-extrabold uppercase tracking-[0.14em] text-beam-graphite/60 ${
                      column.headerClassName || ''
                    }`}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <SkeletonRows columns={selectable ? [{ key: 'select' }, ...columns] : columns} />
              ) : rows.length ? (
                rows.map((row) => {
                  const id = getRowId(row);
                  const checked = selectedIds.includes(id);

                  return (
                    <tr
                      key={id}
                      className={`border-b border-beam-blue/10 align-middle transition ${
                        checked ? 'bg-beam-light/70' : 'hover:bg-beam-light/40'
                      }`}
                    >
                      {selectable ? (
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => onToggleRow(id)}
                            className="focus-ring h-4 w-4 accent-beam-blue"
                            aria-label={t('admin.selectRecord')}
                          />
                        </td>
                      ) : null}
                      {columns.map((column) => (
                        <td key={column.key} className={`px-4 py-3 ${column.className || ''}`}>
                          {column.cell(row)}
                        </td>
                      ))}
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={columnCount}>{empty}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Card view */}
        <div className="md:hidden">
          {isLoading ? (
            <div className="grid gap-3 p-4">
              {Array.from({ length: 4 }).map((_, index) => (
                <div key={index} className="h-28 animate-pulse rounded-lg bg-beam-light" />
              ))}
            </div>
          ) : rows.length ? (
            <ul className="divide-y divide-beam-blue/10">
              {rows.map((row) => {
                const id = getRowId(row);
                const checked = selectedIds.includes(id);

                return (
                  <li key={id} className={`p-4 ${checked ? 'bg-beam-light/70' : 'bg-white'}`}>
                    <div className="flex gap-3">
                      {selectable ? (
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggleRow(id)}
                          className="focus-ring mt-1 h-4 w-4 shrink-0 accent-beam-blue"
                          aria-label={t('admin.selectRecord')}
                        />
                      ) : null}
                      <div className="min-w-0 flex-1">{renderCard ? renderCard(row) : null}</div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            empty
          )}
        </div>
      </div>

      {footer}
    </section>
  );
};
